import { HOME } from '../../views/home/routes';
import { ADESOES } from '../../views/adesoes/routes';
import { ASSOCIADO } from '../../views/associado/routes';
import { CARRO } from '../../views/carro/routes';
import { DOCUMENTO } from '../../views/documento/routes';
import { PRODUTO } from '../../views/produto/routes';
import { TERMO } from '../../views/termo/routes';
import { ASSINATURA } from '../../views/assinatura/routes';
import { CONCLUIDO } from '../../views/concluido/routes';
import { COTACAO } from '../../views/cotacao/routes';
import { COTACAO_PRODUTO } from '../../views/cotacao-produto/routes';
import { VISTORIA } from '../../views/vistoria/routes';
import { CARTEIRA } from '../../views/carteira/routes';
import { ACOMPANHAMENTO_OFICINA } from '../../views/oficina/routes';
import { TI } from '../../views/ti/routes';

/**
 * Rotas já migradas para o `ShellSaac`/`AppShell` (at-55, frente A).
 *
 * ⚠️ É o mecanismo da CONVIVÊNCIA entre o design system novo e as telas legadas.
 * Nestas rotas o `NavGlobal` recolhe o hambúrguer e o drawer, porque o shell já
 * provê navegação — sem isso a tela teria DOIS gatilhos de menu. O sino de
 * novidades continua vindo do `NavGlobal` em todas as rotas (ver lá o porquê).
 *
 * 📌 Cada tela migrada nas frentes B/C/D acrescenta a sua rota aqui. Quando a
 * lista cobrir o app inteiro, o `NavGlobal` e o seu CSS podem ser apagados e
 * o sino se muda para dentro do shell.
 */
/**
 * 🔴 **O casamento é por PREFIXO** (ver `rotaUsaShell`), então registrar
 * `/adesoes` alcança **todas** as rotas `/adesoes/*`. Isso só é correto porque a
 * at-56 migrou `/adesoes/consultar` e removeu `/adesoes/edit` e
 * `/adesoes/edit-novo-associado`: hoje todo `/adesoes/*` é tela migrada.
 *
 * ⚠️ **Ao acrescentar um prefixo aqui, confira as rotas irmãs.** Registrar o pai
 * com filhas ainda legadas as deixaria **sem nenhum acesso ao menu** — o oposto
 * do que este mecanismo existe para evitar (a at-55 o criou para não haver DOIS
 * gatilhos; nesse caso produziria ZERO). Se um dia for preciso registrar só o
 * pai, `rotaUsaShell` precisa de um modo de casamento exato.
 */
/**
 * ✅ **`/associado` cobre a FAMÍLIA inteira (at-58, C1)** — `/associado`,
 * `/associado/edit` e `/associado/substituir` foram migradas juntas, e é por isso
 * que registrar o prefixo é seguro aqui.
 *
 * ⚠️ Não é zelo: as duas filhas são as **primeiras etapas de outros dois fluxos**
 * (Substituição Veicular e Substituição de Titular — `plan-c` §1.4), alcançadas
 * direto pelo menu global. Migrar só o pai as deixaria sem nenhum acesso ao menu.
 */
/**
 * ✅ **`/carro` cobre a FAMÍLIA inteira (at-59, C2)** — `/carro`, `/carro/edit` e
 * `/carro/substituir` foram migradas juntas, pela mesma razão de `/associado`: as
 * duas filhas são as **etapas 2 dos outros dois fluxos** (Substituição Veicular e
 * Substituição de Titular), alcançadas a partir das etapas 1 já migradas.
 */
/**
 * ✅ **A cauda do fluxo entrou na at-60 (C3)** — `/documento` (que cobre também
 * `/documento/edit`), `/produto`, `/termo`, `/assinatura` e `/concluido`.
 *
 * Com isso os **três fluxos** que passam pelo cadastro (adesão, Substituição
 * Veicular e Substituição de Titular) estão migrados de ponta a ponta: as etapas
 * 1 e 2 saíram nas at-58/at-59 e estas são as etapas 3 a 6, compartilhadas.
 *
 * ⚠️ **`/assinatura` NÃO captura `/assinatura-termo`**, e isso é o casamento
 * funcionando: `rotaUsaShell` exige o separador, e
 * `'/assinatura-termo'.startsWith('/assinatura/')` é `false`. Aquelas duas telas
 * são do fluxo de **vistoria** (o nome engana) e seguem legadas até a frente D —
 * parece colisão e não é. **Não "corrigir".**
 */
/**
 * ✅ **O fluxo de COTAÇÃO entrou na at-62 (frente D/D1)** — as duas telas.
 *
 * ⚠️ Aqui elas são rotas **IRMÃS**, não pai e filha: `/cotacao-produtos` não tem
 * o separador, então `'/cotacao-produtos'.startsWith('/cotacao/')` é `false` e o
 * prefixo `/cotacao` **não** a alcança. Por isso as duas precisam estar na
 * lista — registrar só a primeira deixaria a segunda sem nenhum acesso ao menu
 * (o `NavGlobal` recolhe o hambúrguer nas rotas migradas). Há teste sentinela
 * em `routes.test.ts`.
 *
 * 📌 É o mesmo mecanismo que protege `/assinatura-termo` de ser capturada por
 * `/assinatura` — só que ali o efeito desejado é o oposto.
 */
/**
 * ✅ **A família `/vistoria` fechou na at-64 (frente D/D2)** — e com ela o
 * casamento EXATO ficou sem uso.
 *
 * A at-63 migrou o painel `/vistoria` e a captura enquanto 4 filhas seguiam
 * legadas, e foi isso que obrigou o "modo exato": registrar o pai por prefixo
 * capturaria as legadas e as deixaria **sem nenhum acesso ao menu** (o
 * `NavGlobal` recolhe o hambúrguer nas rotas migradas, então o efeito seria ZERO
 * gatilhos). Esta entrega migrou as 5 restantes — checklist, assinatura, prévia,
 * laudo e a visualização por veículo —, então o **prefixo `/vistoria` cobre as 7
 * telas** e as duas entradas da at-63 colapsaram numa só, como estava previsto.
 *
 * 🔴 **`/assinatura-termo` não existe mais.** Aquelas duas telas eram etapas da
 * vistoria com nome de outro fluxo, e viraram `/vistoria/assinatura` e
 * `/vistoria/assinatura/previa` (ver `views/vistoria/routes.ts`). Com isso os
 * sentinelas que existiam aqui e em `routes.test.ts` — explicando que
 * `/assinatura` **não** capturava `/assinatura-termo` — deixaram de descrever o
 * app. O mecanismo que os motivava **continua valendo** e segue coberto por
 * teste: `rotaUsaShell` exige o separador, então um prefixo nunca alcança uma
 * rota irmã de nome parecido.
 */
/**
 * ✅ **`/carteira` entrou na at-65 (frente D)** — a tela "Minha Carteira".
 *
 * 📌 É uma rota **sem filhas nem irmãs de prefixo**: `/acompanhamento-oficina`, que
 * mostra o outro recorte da mesma carteira, tem prefixo próprio — por isso ela
 * precisou de entrada própria (at-66, abaixo).
 *
 * ⚠️ O sentinela de `routes.test.ts` que afirmava `rotaUsaShell('/carteira') ===
 * false` foi **invertido** nesta entrega — ele descrevia o app enquanto a tela era
 * legada e passaria a mentir.
 */
/**
 * ✅ **`/acompanhamento-oficina` entrou na at-66 (frente D)** — e com ela **todas as
 * telas do CONSULTOR estão no shell**.
 *
 * 📌 O que resta legado são as **filhas** de `/ti/*` e o `/login` (o hub `/ti` entrou
 * na at-67, abaixo). É esse o marco que destrava planejar a remoção do `NavGlobal` e
 * a mudança do sino para dentro do shell: enquanto houver tela legada, o `NavGlobal`
 * precisa continuar montado para elas.
 *
 * ⚠️ Como a `/carteira`, é rota **sem filhas nem irmãs de prefixo** — o nome parecido
 * com `/carteira` não cria colisão porque o casamento exige o separador.
 */
const ROTAS_COM_SHELL: string[] = [
  HOME(),
  '/',
  ADESOES(),
  ASSOCIADO(),
  CARRO(),
  DOCUMENTO(),
  PRODUTO(),
  TERMO(),
  ASSINATURA(),
  CONCLUIDO(),
  COTACAO(),
  COTACAO_PRODUTO(),
  VISTORIA(),
  CARTEIRA(),
  ACOMPANHAMENTO_OFICINA(),
  /*
   * ✅ **`/ti` COBRE A FAMÍLIA INTEIRA desde a at-70 (D8)** — o hub e as 8 filhas.
   *
   * As entradas individuais que existiam aqui (`/ti/usuarios` na at-68; `/ti/termos`,
   * `/ti/contratos`, `/ti/planos` e `/ti/bikes` na at-69) **foram substituídas por
   * esta**: com `/ti/envios` e `/ti/crons` migradas, não sobra filha legada, e o
   * prefixo do pai alcança todas — inclusive as sub-rotas das fichas
   * (`/ti/termos/:codigo`, `/ti/planos/:codigo`, `/ti/bikes/novo`, `/ti/bikes/:id`,
   * `/ti/usuarios/:id`) e as que vierem.
   *
   * 📌 É o mesmo **colapso** que a at-64 fez com `/vistoria`, quando as 5 telas
   * restantes daquele fluxo migraram: enquanto havia filha legada, o pai vivia em
   * `ROTAS_COM_SHELL_EXATAS` (at-67); agora ele volta para cá e aquela lista fica
   * vazia.
   */
  TI(),
];

/**
 * 🔴 Rotas migradas que casam **SÓ EXATAMENTE** — nunca por prefixo.
 *
 * Este é o "modo de casamento exato" que o comentário da regra das famílias
 * previa (*"se um dia for preciso registrar só o pai, `rotaUsaShell` precisa
 * ganhar um modo de casamento exato"*). **O "um dia" chegou na at-63**, quando
 * Victor pediu o painel `/vistoria` migrado enquanto 4 das suas filhas seguem
 * legadas.
 *
 * ⚠️ **Use isto só quando o pai estiver migrado e alguma filha NÃO.** Enquanto a
 * família inteira estiver migrada, o prefixo é preferível: é uma entrada só e
 * cobre as filhas futuras automaticamente. Uma rota exata deixa filha nova
 * **fora** do shell sem ninguém perceber — o que é o comportamento certo durante
 * uma migração parcial e o errado depois dela.
 *
 * 📌 Ficou vazia entre a at-64 e a at-66; recebeu o hub `/ti` na at-67 (o caso que
 * este comentário previa, "provável na frente D") e **voltou a ficar vazia na
 * at-70**, quando `/ti/envios` e `/ti/crons` — as duas últimas filhas legadas —
 * migraram e `/ti` pôde voltar ao prefixo.
 *
 * 🔴 **A lista vazia NÃO é código morto: é o mecanismo esperando a próxima migração
 * parcial.** Ele já foi usado duas vezes (at-63 com `/vistoria`, at-67 com `/ti`) e
 * as duas terminaram em colapso para o prefixo. Apagá-lo faria a terceira migração
 * parcial ter de reinventá-lo — e o custo de errar isso é uma tela **sem nenhum
 * acesso ao menu**, porque o `NavGlobal` recolhe o hambúrguer nas rotas migradas.
 *
 * 📌 **Depois da at-70 só o `/login` fica fora do shell** — é o marco que destrava
 * a conversa sobre apagar o `NavGlobal` e mudar o sino para dentro do shell
 * (fora do escopo daquela branch; enquanto o login for legado, o `NavGlobal`
 * precisa continuar montado para ele).
 */
const ROTAS_COM_SHELL_EXATAS: string[] = [];

export function rotaUsaShell(pathname: string): boolean {
  if (ROTAS_COM_SHELL_EXATAS.includes(pathname)) return true;

  return ROTAS_COM_SHELL.some(
    (rota) => pathname === rota || (rota !== '/' && pathname.startsWith(`${rota}/`)),
  );
}

export { ROTAS_COM_SHELL_EXATAS };
export default ROTAS_COM_SHELL;
